const GEOLOCATION_TIMEOUT = 15000;
const GEOLOCATION_MAXIMUM_AGE = 5 * 60 * 1000;

export default class GeoLocator {
    constructor(googleMapsLoader = null) {
        if (googleMapsLoader !== null && typeof googleMapsLoader !== 'function') {
            throw new TypeError('googleMapsLoader must be a function or null');
        }

        this.googleMapsLoader = googleMapsLoader;
        this.geocoderPromise = null;
    }

    getUserCountryBounds() {
        return this.getCurrentPosition().then((coords) =>
            this.reverseGeocodeCountry(coords)
        );
    }

    getCurrentPosition() {
        const navigator = window.navigator;

        if (!navigator || !navigator.geolocation) {
            return Promise.reject(new Error('Geolocation not supported'));
        }

        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;

                    resolve({ latitude, longitude });
                },
                (error) => {
                    reject(error);
                },
                {
                    enableHighAccuracy: false,
                    timeout: GEOLOCATION_TIMEOUT,
                    maximumAge: GEOLOCATION_MAXIMUM_AGE,
                }
            );
        });
    }

    reverseGeocodeCountry(coords) {
        return this.getGeocoder().then((geocoder) =>
            new Promise((resolve, reject) => {
                geocoder.geocode(
                    {
                        location: {
                            lat: coords.latitude,
                            lng: coords.longitude,
                        },
                    },
                    (results, status) => {
                        if (status !== 'OK' || !Array.isArray(results)) {
                            return reject(new Error('Geocoding failed'));
                        }

                        const countryResult = results.find(
                            (result) =>
                                Array.isArray(result.types) &&
                                result.types.includes('country')
                        );

                        if (!countryResult) {
                            return reject(new Error('Country not found'));
                        }

                        const { geometry } = countryResult;

                        if (!geometry) {
                            return reject(new Error('Geometry not found'));
                        }

                        const bounds = geometry.viewport || geometry.bounds;

                        if (!bounds) {
                            return reject(new Error('Bounds not found'));
                        }

                        resolve({
                            countryCode: this.extractCountryCode(countryResult),
                            bounds: this.formatBounds(bounds),
                        });
                    }
                );
            })
        );
    }

    getGeocoder() {
        if (!this.geocoderPromise) {
            const loaderPromise = this.googleMapsLoader
                ? this.googleMapsLoader()
                : Promise.resolve();

            this.geocoderPromise = loaderPromise.then(() => {
                const { google } = window;

                if (!google?.maps?.Geocoder) {
                    throw new Error('Google Maps API is not ready');
                }

                return new google.maps.Geocoder();
            });
        }

        return this.geocoderPromise;
    }

    extractCountryCode(result) {
        const components = result.address_components || [];

        const countryComponent = components.find(
            (component) =>
                Array.isArray(component.types) &&
                component.types.includes('country')
        );

        return countryComponent ? countryComponent.short_name : null;
    }

    formatBounds(bounds) {
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();

        return {
            north: northEast.lat(),
            east: northEast.lng(),
            south: southWest.lat(),
            west: southWest.lng(),
        };
    }
}
